import { useState } from 'react';
import { useToast } from '@/components/shared/use-toast';
import { supabase } from '@/lib/supabase';
import { Weights, OpportunityScore } from '@/types/opportunity';
import { useNavigate } from 'react-router-dom';
import { storePendingAssessment } from '@/utils/assessmentStorage';

export const useShareAssessment = (weights: Weights, scoredOpportunities: OpportunityScore[]) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSharing, setIsSharing] = useState(false);

  const handleShare = async (): Promise<string> => {
    setIsSharing(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to share your assessment",
          variant: "destructive",
        });
        storePendingAssessment({
          weights,
          scoredOpportunities,
          returnTo: '/assessment'
        });
        navigate('/login');
        throw new Error('Authentication required');
      }

      console.log('Creating shared assessment with data:', {
        weights,
        scored_opportunities: scoredOpportunities,
        type: 'opportunity'
      });

      const { data, error } = await supabase
        .from('shared_assessments')
        .insert({
          weights,
          scored_opportunities: scoredOpportunities,
          type: 'opportunity',
          created_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        console.error('Supabase error creating shared assessment:', error);
        throw error;
      }

      if (!data || !data.id) {
        console.error('No data returned from insert operation');
        throw new Error('Failed to create shared assessment');
      }

      console.log('Shared assessment created successfully:', data);

      const shareUrl = `${window.location.origin}/shared/opportunity/${data.id}`;
      await navigator.clipboard.writeText(shareUrl);

      return shareUrl;
    } catch (error) {
      console.error('Detailed error in handleShare:', error);
      throw error;
    } finally {
      setIsSharing(false);
    }
  };

  return { handleShare, isSharing };
};